import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { GameProvider } from './context/GameContext';
import { UIProvider } from './context/UIContext';
import { EditProvider } from './context/EditContext';
import { NotificationProvider } from './context/NotificationContext';
import { useAuth } from './context/AuthContext';
import theme from './theme';
import './styles/App.css';

// Public routes — keep eager so the landing page stays light
import Home from './pages/Home';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import DPA from './pages/DPA';
import CheckYourEmail from './components/CheckYourEmail';
import AuthCallback from './components/AuthCallback';
import VerificationMessage from './components/VerificationMessage';
import InvitationHandler from './components/InvitationHandler';
import { ProtectedRoute, MobileNavigation } from './components/ui';

const JoinGameGuest = lazy(() =>
  import('./pages/gameplay_pages').then((m) => ({ default: m.JoinGameGuest }))
);

const OrganizationSetup = lazy(() => import('./pages/OrganizationSetup'));
const ManageUsers = lazy(() => import('./pages/ManageUsers'));
const ManageGameSpaceUsers = lazy(() => import('./pages/ManageGameSpaceUsers'));
const ManageLearnspaces = lazy(() => import('./pages/ManageLearnspaces'));
const LearningContentLibraryAdmin = lazy(() => import('./pages/LearningContentLibraryAdmin'));
const CapabilityTaxonomyAdmin = lazy(() => import('./pages/CapabilityTaxonomyAdmin'));
const EntityProfilesAdmin = lazy(() => import('./pages/EntityProfilesAdmin'));
const LearningHistory = lazy(() => import('./pages/LearningHistory'));
const LearningHistoryDetail = lazy(() => import('./pages/LearningHistoryDetail'));
const Billing = lazy(() => import('./pages/Billing'));
const UserProfilePopup = lazy(() => import('./components/UserProfilePopup'));
const SimulationPlayground = lazy(() => import('./pages/SimulationPlayground'));

const GameCards = lazy(() =>
  import('./pages/game_pages').then((m) => ({ default: m.GameCards }))
);
const GameIntro = lazy(() =>
  import('./pages/game_pages').then((m) => ({ default: m.GameIntro }))
);

const GamePlay = lazy(() =>
  import('./pages/gameplay_pages').then((m) => ({ default: m.GamePlay }))
);
const JoinGame = lazy(() =>
  import('./pages/gameplay_pages').then((m) => ({ default: m.JoinGame }))
);
const WaitingRoom = lazy(() =>
  import('./pages/gameplay_pages').then((m) => ({ default: m.WaitingRoom }))
);

const GameCreation = lazy(() =>
  import('./pages/gamecreation_pages').then((m) => ({ default: m.GameCreation }))
);
const GameCreationContinue = lazy(() =>
  import('./pages/gamecreation_pages').then((m) => ({ default: m.GameCreationContinue }))
);

const GameCreationReview = lazy(() =>
  import('./pages/gameedit_pages').then((m) => ({ default: m.GameCreationReview }))
);
const GameCapabilities = lazy(() =>
  import('./pages/gameedit_pages').then((m) => ({ default: m.GameCapabilities }))
);

const SessionHistory = lazy(() =>
  import('./components/gameplay').then((m) => ({ default: m.SessionHistory }))
);

const MomentPaths = lazy(() => import('./pages/moments/MomentPaths'));
const MomentPathPage = lazy(() => import('./pages/moments/MomentPathPage'));
const MomentPlayer = lazy(() => import('./pages/moments/MomentPlayer'));
const MomentCompare = lazy(() => import('./pages/moments/MomentCompare'));
const MomentSuiteReview = lazy(() => import('./pages/moments/MomentSuiteReview'));

const RouteFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 240,
      py: 6,
    }}
  >
    <CircularProgress aria-label="Loading page" />
  </Box>
);

function App() {
  const { isAuthenticated, organizationId, sessionExpired, reauthenticate } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const errorDescription = searchParams.get('error_description');

    // Invitation flow owns routing
    if (searchParams.has("invitation") && searchParams.has("organization")) return;

    // Org-required error → email check page
    if (errorDescription?.includes("client requires organization membership")) {
      navigate('/check-your-email');
      return;
    }

    if (!isAuthenticated) return;

    // Never auto-redirect to /setup-organization; allow manual access
    if (location.pathname === '/setup-organization') return;

    // Wait until orgId is resolved before any default redirects
    if (typeof organizationId === 'undefined') return;

    // Default landing: if user ends up at "/", send them to /game-cards once.
    if (location.pathname === '/') {
      navigate('/game-cards', { replace: true });
    }
  }, [isAuthenticated, organizationId, location.pathname, location.search, navigate]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {sessionExpired && (
        <Box sx={{ position: 'sticky', top: 0, zIndex: (theme) => theme.zIndex.snackbar, p: 2 }}>
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={reauthenticate}>
                Log in again
              </Button>
            }
          >
            Your session has expired. Please log in again.
          </Alert>
        </Box>
      )}
      <EditProvider>
        <NotificationProvider>
        <GameProvider>
          <MobileNavigation />
          <div className={isAuthenticated ? 'App App--authenticated' : 'App'}>
            <InvitationHandler />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
            {/* Home with verification message */}
            <Route path="/" element={<><Home /><VerificationMessage /></>} />
            <Route path="/guest" element={<JoinGameGuest />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/dpa" element={<DPA />} />
            {/* Game play */}
            <Route path="/game-play/:session_id/user/:user_id" element={<UIProvider><GamePlay /></UIProvider>} />

            {/* Authentication flow */}
            <Route path="/check-your-email" element={<CheckYourEmail />} />
            <Route path="/callback" element={<AuthCallback />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            {/* Protected routes (grouped) */}
            <Route element={<ProtectedRoute />}>

                <Route path="/billing" element={<Billing />} />
                
              
                <Route path="/setup-organization" element={<OrganizationSetup />} />
                <Route path="/manage-users" element={<ManageUsers />} />
                <Route path="/gamespaces/:spaceId/manage-users" element={<ManageGameSpaceUsers />} />
                <Route path="/admin/learnspaces" element={<ManageLearnspaces />} />
                <Route path="/admin/learning-content" element={<LearningContentLibraryAdmin />} />
                <Route path="/admin/capability-taxonomies" element={<CapabilityTaxonomyAdmin />} />
                <Route path="/admin/entity-profiles" element={<EntityProfilesAdmin />} />
                <Route path="/user-profile" element={<UserProfilePopup />} />

                {/* Game flow */}
                <Route path="/game-cards" element={<GameCards />} />
                <Route path="/moments" element={<MomentPaths />} />
                <Route path="/moments/paths/:pathId" element={<MomentPathPage />} />
                <Route path="/moments/suites/:pathId" element={<MomentSuiteReview />} />
                <Route path="/moments/compare/:comparisonId" element={<MomentCompare />} />
                <Route path="/moments/:versionId" element={<MomentPlayer />} />
                <Route path="/game-intro/:game_id" element={<GameIntro />} />
                <Route path="/games/:game_id/capabilities" element={<GameCapabilities />} />
                <Route path="/waiting-room/:session_id" element={<WaitingRoom />} />
                <Route path="/join-game" element={<JoinGame />} />
                
                <Route path="/player/sessions" element={<SessionHistory />} />
                <Route path="/learning/history" element={<LearningHistory />} />
                <Route
                  path="/learning/history/:learning_experience_id"
                  element={<LearningHistoryDetail />}
                />

                {/* Game creation flow */}
                <Route path="/create-game" element={<GameCreation />} />
                <Route
                  path="/create-game-outline/:game_id"
                  element={<Navigate to="/game-creation-continue/:game_id" replace />}
                />
                <Route
                  path="/create-game-chapters/:game_id/chapter/:creation_current_chapter"
                  element={<Navigate to="/game-creation-continue/:game_id" replace />}
                />
                <Route
                  path="/create-game-conclusion/:game_id"
                  element={<Navigate to="/game-creation-continue/:game_id" replace />}
                />
                <Route
                  path="/create-game-introduction/:game_id"
                  element={<Navigate to="/game-creation-continue/:game_id" replace />}
                />
                <Route
                  path="/finalize-game/:game_id"
                  element={<Navigate to="/game-creation-continue/:game_id" replace />}
                />
                <Route path="/create-game-review/:game_id" element={<UIProvider><GameCreationReview /></UIProvider>} />
                <Route path="/game-creation-continue/:game_id" element={<UIProvider><GameCreationContinue /></UIProvider>} />

                {/* Simulation routes */}
                <Route path="/simulation" element={<SimulationPlayground />} />
                
            </Route>
            </Routes>
            </Suspense>
          </div>
        </GameProvider>
        </NotificationProvider>
      </EditProvider>
    </ThemeProvider>
  );
}

export default App;
